import useLocalization from "@/hooks/useLocalization";
import { Box, alpha, useTheme } from "@mui/material";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DashboardChartTooltipProps,
  DashboardChartMetrics,
  DashboardGraphScope,
} from "../types";
import { addBucketLabels, formatInteger } from "../utils";
import ChartCard from "./ChartCard";
import DashboardTooltip from "./DashboardTooltip";

type GuestsOverTimeChartProps = {
  graphScope: DashboardGraphScope;
  metrics: DashboardChartMetrics[];
};

type GuestsChartMetric = DashboardChartMetrics & {
  averageGroupSize: number | null;
  bucketLabel: string;
  interpolatedAverageGroupSize: number | null;
};

type GuestsTooltipPayload = {
  color?: string;
  dataKey?: string;
  payload?: GuestsChartMetric;
  value?: number | string;
};

const getAverageGroupSizeTicks = (values: number[]) => {
  if (values.length === 0) return [0, 1];

  const max = Math.max(...values);
  const step = Math.max(1, Math.ceil(max / 4));
  const upper = max === 0 ? step : Math.ceil(max / step) * step;
  const ticks: number[] = [];
  let tick = 0;

  while (tick <= upper) {
    ticks.push(tick);
    tick += step;
  }

  return ticks;
};

const interpolateAverageGroupSize = (
  data: Omit<GuestsChartMetric, "interpolatedAverageGroupSize">[],
): GuestsChartMetric[] => {
  const nextData: GuestsChartMetric[] = data.map((metric) => ({
    ...metric,
    interpolatedAverageGroupSize: null,
  }));

  data.forEach((metric, index) => {
    if (metric.averageGroupSize !== null) return;

    let previousIndex = index - 1;
    while (
      previousIndex >= 0 &&
      data[previousIndex]?.averageGroupSize === null
    ) {
      previousIndex -= 1;
    }

    let nextIndex = index + 1;
    while (
      nextIndex < data.length &&
      data[nextIndex]?.averageGroupSize === null
    ) {
      nextIndex += 1;
    }

    const previousValue = data[previousIndex]?.averageGroupSize;
    const nextValue = data[nextIndex]?.averageGroupSize;
    if (previousValue === null || previousValue === undefined) return;
    if (nextValue === null || nextValue === undefined) return;

    const previousMetric = nextData[previousIndex];
    const nextMetric = nextData[nextIndex];
    const currentMetric = nextData[index];
    if (!previousMetric || !nextMetric || !currentMetric) return;

    previousMetric.interpolatedAverageGroupSize = previousValue;
    nextMetric.interpolatedAverageGroupSize = nextValue;

    const interpolationProgress =
      (index - previousIndex) / (nextIndex - previousIndex);
    currentMetric.interpolatedAverageGroupSize = Number(
      (
        previousValue +
        (nextValue - previousValue) * interpolationProgress
      ).toFixed(1),
    );
  });

  return nextData;
};

export default function GuestsOverTimeChart({
  graphScope,
  metrics,
}: GuestsOverTimeChartProps) {
  const theme = useTheme();
  const { t, adapterLocale } = useLocalization();
  const averageGroupSizeColor = theme.palette.info.main;
  const chartData = interpolateAverageGroupSize(
    addBucketLabels(metrics, graphScope, adapterLocale).map((metric) => ({
      ...metric,
      averageGroupSize:
        metric.reservations === 0
          ? null
          : Number((metric.guests / metric.reservations).toFixed(1)),
    })),
  );
  const averageGroupSizeTicks = getAverageGroupSizeTicks(
    chartData.flatMap(
      (metric) =>
        metric.averageGroupSize ?? metric.interpolatedAverageGroupSize ?? [],
    ),
  );
  const averageGroupSizeDomain: [number, number] = [
    averageGroupSizeTicks[0] ?? 0,
    averageGroupSizeTicks[averageGroupSizeTicks.length - 1] ?? 1,
  ];
  const renderTooltip = ({
    active,
    label,
    payload,
  }: DashboardChartTooltipProps) => {
    if (!active || !payload?.length) return null;

    const tooltipPayload = payload as GuestsTooltipPayload[];
    const tooltipItems = tooltipPayload.flatMap((item) => {
      if (
        item.dataKey === "interpolatedAverageGroupSize" &&
        item.payload?.averageGroupSize !== null
      ) {
        return [];
      }

      return [
        {
          color: item.color,
          label:
            item.dataKey === "averageGroupSize" ||
            item.dataKey === "interpolatedAverageGroupSize"
              ? t("dashboard:guestsPerGroup")
              : t("dashboard:guests"),
          value:
            item.dataKey === "averageGroupSize" ||
            item.dataKey === "interpolatedAverageGroupSize"
              ? Number(item.value).toFixed(1)
              : formatInteger(Number(item.value)),
        },
      ];
    });

    return <DashboardTooltip title={label} items={tooltipItems} />;
  };

  return (
    <ChartCard title={t("dashboard:guestsOverTime")}>
      <Box sx={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 8, right: 0, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="guestsGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={theme.palette.primary.main}
                  stopOpacity={0.3}
                />
                <stop
                  offset="95%"
                  stopColor={theme.palette.primary.main}
                  stopOpacity={0}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.palette.divider}
            />
            <XAxis dataKey="bucketLabel" stroke={theme.palette.text.secondary} />
            <YAxis
              yAxisId="guests"
              tickFormatter={(value) => formatInteger(Number(value))}
              stroke={theme.palette.text.secondary}
              width={64}
            />
            <YAxis
              yAxisId="averageGroupSize"
              domain={averageGroupSizeDomain}
              orientation="right"
              ticks={averageGroupSizeTicks}
              tickFormatter={(value) => formatInteger(Number(value))}
              stroke={theme.palette.text.secondary}
              width={64}
            />
            <Tooltip
              content={renderTooltip}
              contentStyle={{
                borderColor: theme.palette.divider,
                borderRadius: 8,
                boxShadow: theme.shadows[2],
              }}
              cursor={{
                stroke: alpha(theme.palette.primary.main, 0.35),
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="guests"
              name={t("dashboard:guests")}
              yAxisId="guests"
              stroke={theme.palette.primary.main}
              fill="url(#guestsGradient)"
              isAnimationActive={false}
              strokeWidth={2}
            />
            <Line
              type="monotone"
              dataKey="averageGroupSize"
              name={t("dashboard:guestsPerGroup")}
              yAxisId="averageGroupSize"
              stroke={averageGroupSizeColor}
              strokeWidth={2}
              dot={{ r: 3 }}
              isAnimationActive={false}
            />
            <Line
              type="monotone"
              dataKey="interpolatedAverageGroupSize"
              name={t("dashboard:guestsPerGroup")}
              yAxisId="averageGroupSize"
              stroke={averageGroupSizeColor}
              strokeDasharray="2 4"
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
              legendType="none"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </ChartCard>
  );
}
